import { inject, Injectable, Injector } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpHeaders, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { combineLatest, EMPTY, from, Observable, of, throwError } from 'rxjs';
import { environment } from '../environments/environment';
import { catchError, finalize, map, switchMap, take, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import * as AuthActions from '../app/store/auth/auth.actions'
import { LoaderService } from '../app/shared/services/loader/loader.service';
import { Router } from '@angular/router';
import { AuthState } from '../app/store/auth/auth.reducer';
import * as AuthSelectors from '../app/store/auth/auth.selectors';
import { AppStateService } from 'src/app/shared/services/app-state.service';
import { SKIP_LOADER, SKIP_TOKEN_REFRESH, TRUSTED_BROWSER } from 'src/app/shared/models/httpContextTokens';
import { MainSubscriptionsService } from 'src/app/shared/services/main-subscriptions/main-subscriptions.service';
import { ServiceEntitlementService } from 'src/app/shared/services/service-entitlement.service';
import { SessionRefreshService } from 'src/app/shared/services/session-refresh.service';
import { NetworkService } from 'src/app/shared/services/network.service';
import { ToastService } from 'src/app/shared/services/toast.service';

@Injectable()
export class HttpConfigInterceptor implements HttpInterceptor {
  private store = inject(Store);
  private loaderService = inject(LoaderService);
  private router = inject(Router);
  private authStore = inject(Store<AuthState>);
  private appStateService = inject(AppStateService);
  private network = inject(NetworkService);
  // Injected on demand: as fields they would close a cycle with HttpClient.
  private injector = inject(Injector);

  // One redirect per revocation, not one per failing call.
  private redirectingToCatalog: boolean = false;

  private headers = new HttpHeaders({
    'Content-Type': 'application/json'
  })

  private isLoaderNotRequired(url: string): boolean {
    return [
      'system.domains.api',
      'system.roles.api',
      'uv.domain.style.api',
      'system.resources.api',
      'uv.domain.property.list.api',
      'uv.dashboard.list.api',
      'uv.widget.list.api',
      'uv.device.list.api',
      'uv.devicebrand.list.api',
      'uv.devicemodel.list.api',
      'system.property.api',
      'uv.utility.prefix.list.api',
      'uv.user.notification.preference',
      'uv.device.stream.api',
      // AMPECO charge point aggregate is polled on a timer - keep it quiet.
      'uv.integration.ampeco.chargepoint.api',
      'download/thumbnail'
    ].some(endpoint => url.includes(endpoint));
  }

  private isCacheable(url: string): boolean {
    return url.includes('download/thumbnail');
  }

  private isNotificationsFlow(url: string): boolean {
    return url.includes('uv.notification.list.api');
  }

  private isServiceNotActive(error: HttpErrorResponse): boolean {
    return error?.status === 403 &&
      typeof error?.error?.Message === 'string' &&
      error.error.Message.startsWith('SERVICE_NOT_ACTIVE');
  }

  private handleServiceNotActive(): void {
    if (this.redirectingToCatalog) {
      return;
    }
    this.redirectingToCatalog = true;

    this.injector.get(ServiceEntitlementService).reload().pipe(take(1)).subscribe();
    this.injector.get(MainSubscriptionsService)
      .setCustomErrorComand(ServiceEntitlementService.NOT_ACTIVE_MESSAGE);

    this.router.navigate([ServiceEntitlementService.CATALOG_ROUTE])
      .finally(() => this.redirectingToCatalog = false);
  }

  // Compared by equality, never by prefix: "Invalid token for resource <url>"
  // denies one call on a valid session and must not end it.
  private static readonly SESSION_GONE_MESSAGES: ReadonlyArray<string> = [
    'Invalid token',
    'Token expired',
    'Token has expired.',
    'Token not provided',
    'Required token is missing',
    // The domain of the session was disabled; Administrators never get it.
    'DOMAIN_DISABLED'
  ];

  // A 401 or 403 carrying one of the messages above means the session is gone,
  // which is what a deleted user gets on every call. A bare one denies a single
  // call: Zesty answers 401 to a wrong old password and to a resource the role
  // lacks ("Access denied"), and neither must end the session.
  private isSessionGone(error: HttpErrorResponse): boolean {
    if (error?.status !== 401 && error?.status !== 403) {
      return false;
    }

    const message: unknown = error?.error?.Message;

    return typeof message === 'string' &&
      HttpConfigInterceptor.SESSION_GONE_MESSAGES.includes(message);
  }

  // Both spellings reach the client: the manual check in the middleware raises
  // the first, the JWT library the second.
  private isTokenExpired(error: HttpErrorResponse): boolean {
    const message: unknown = error?.error?.Message;

    return message === 'Token expired' || message === 'Token has expired.';
  }

  // The second step of a sign-in: called with a ticket, before any session.
  private static readonly SECOND_STEP: ReadonlyArray<string> = [
    'uv.twofactor.verify.api',
    'uv.twofactor.resend.api',
    'uv.twofactor.recovery.api',
    'uv.twofactor.enrol.start.api',
    'uv.twofactor.enrol.confirm.api'
  ];

  private isSecondStep(url: string | null | undefined): boolean {
    return HttpConfigInterceptor.SECOND_STEP.some(endpoint => url?.includes(endpoint));
  }

  private isSignIn(url: string | null | undefined): boolean {
    return !!url?.includes('system.login.api') || this.isSecondStep(url);
  }

  // Renewing the session is worth one retry. The auth endpoints are excluded:
  // refreshing around them is either circular or pointless.
  private static readonly NEVER_RETRIED: ReadonlyArray<string> = [
    'system.refresh.api',
    'system.login.api',
    'system.logout.api',
    ...HttpConfigInterceptor.SECOND_STEP
  ];

  private canRetryAfterRefresh(request: HttpRequest<any>, error: HttpErrorResponse): boolean {
    return this.isTokenExpired(error) &&
      !request.context.get(SKIP_TOKEN_REFRESH) &&
      !HttpConfigInterceptor.NEVER_RETRIED.some(url => request.url.includes(url));
  }

  private refreshAndRetry(
    request: HttpRequest<any>,
    next: HttpHandler,
    originalError: HttpErrorResponse
  ): Observable<HttpEvent<any>> {
    return this.injector.get(SessionRefreshService).refresh().pipe(
      take(1),
      // A refused refresh has already ended the session in the service, and a
      // network error there leaves it alone: this call just reports what it hit.
      catchError(() => throwError(() => originalError)),
      switchMap(token => next.handle(request.clone({
        headers: request.headers.set('ZestyApiBearer', token),
        context: request.context.set(SKIP_TOKEN_REFRESH, true)
      })).pipe(
        catchError((error: HttpErrorResponse) => this.readBlobError(error).pipe(
          switchMap(readable => this.settleError(readable))
        ))
      ))
    );
  }

  // A blob request gets its error body as a Blob too. Without reading it the
  // checks see a bare 401 and cannot tell an expired token, so the session
  // would neither be renewed nor ended.
  private readBlobError(error: HttpErrorResponse): Observable<HttpErrorResponse> {
    const body: unknown = error?.error;

    if (!(body instanceof Blob) || !body.type.includes('json')) {
      return of(error);
    }

    return from(body.text()).pipe(
      map(text => new HttpErrorResponse({
        error: JSON.parse(text),
        headers: error.headers,
        status: error.status,
        statusText: error.statusText,
        url: error.url ?? undefined
      })),
      catchError(() => of(error))
    );
  }

  private settleError(error: HttpErrorResponse): Observable<never> {
    // Completed, not rethrown: the caller would surface the raw code as a
    // banner and keep firing calls during the redirect.
    if (this.isServiceNotActive(error)) {
      this.handleServiceNotActive();
      return EMPTY;
    }

    // The same for a session whose domain was disabled: it ends with the reason
    // on screen. A refused sign-in or refresh is for the login page and
    // SessionRefreshService to settle.
    if (error?.error?.Message === 'DOMAIN_DISABLED' &&
      !this.isSignIn(error?.url) &&
      !error?.url?.includes('system.refresh.api')) {
      this.handleError(error);
      return EMPTY;
    }

    this.handleError(error);
    return throwError(() => error);
  }

  // Fired by the guard before the token is in place on deep-link or hard reload.
  private isEntitlementCall(error: HttpErrorResponse): boolean {
    return typeof error?.url === 'string' &&
      (error.url.includes('uv.domain.services.active.api') ||
        error.url.includes('uv.domain.service.catalog.api'));
  }

  // The logout empties the store at once, while the page still on screen may
  // fire a last call without a bearer, or have one in flight with the bearer
  // just withdrawn: the refusal belongs to a session already over and has
  // nothing to tell the user. A sign-in has no bearer yet: its DOMAIN_DISABLED
  // is for the login page.
  private isAfterLogout(error: HttpErrorResponse): Observable<boolean> {
    if (!this.isSessionGone(error) || this.isEntitlementCall(error) || this.isSignIn(error?.url)) {
      return of(false);
    }

    return this.authStore.select(AuthSelectors.getCurrentJwtToken).pipe(
      take(1),
      map(token => !token)
    );
  }

  private handleError(error: HttpErrorResponse): void {
    // A refused refresh is settled by SessionRefreshService, once.
    if (error?.url?.includes('system.refresh.api')) {
      return;
    }

    if (error?.error?.Message === 'Password expired') {
      this.router.navigate(['auth/reset-expired']);
    }
    // A failed login or second step never had a session; a failed logout is
    // ending one already. An entitlement call may run before the bearer is in
    // place, but DOMAIN_DISABLED only ever answers one.
    else if (this.isSessionGone(error) &&
      !this.isSignIn(error?.url) &&
      !error?.url?.includes('system.logout.api') &&
      (!this.isEntitlementCall(error) || error.error.Message === 'DOMAIN_DISABLED')) {
      this.store.dispatch(AuthActions.logout());

      if (error.error.Message === 'DOMAIN_DISABLED') {
        this.injector.get(ToastService).error('LOGIN.DOMAIN_DISABLED');
      }
    }
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return combineLatest([
      this.authStore.select(AuthSelectors.getCurrentJwtToken).pipe(take(1)),
      this.store.select(AuthSelectors.isAuth).pipe(take(1)),
      this.appStateService.isInitializing.pipe(take(1))
    ]).pipe(
      switchMap(([token, isAuthenticated, isInitializing]) => {
        const shouldShowLoader =
          !request.context.get(SKIP_LOADER) &&
          !this.isLoaderNotRequired(request.url) &&
          !this.isNotificationsFlow(request.url) &&
          (!isInitializing || isAuthenticated);

        if (shouldShowLoader) {
          this.loaderService.show();
        }

        let headers = this.headers;
        if (token) {
          headers = headers.set('ZestyApiBearer', token);
        }

        const trustedBrowser = request.context.get(TRUSTED_BROWSER);
        if (trustedBrowser) {
          headers = headers.set('X-UV-Trust', trustedBrowser);
        }

        const clonedRequest = request.clone({
          withCredentials: true,
          url: `${environment.apiUrl}/${request.url}`,
          headers: headers,
          ...(this.isCacheable(request.url)
            ? {}
            : {
              setHeaders: {
                'Cache-Control': 'no-cache',
                Pragma: 'no-cache'
              }
            }
          )
        });

        return next.handle(clonedRequest).pipe(
          tap(event => {
            if (event instanceof HttpResponse) {
              this.network.reportRequest(true);
            }
          }),
          catchError((error: HttpErrorResponse) => {
            // Status 0 is the only case with no response at all.
            this.network.reportRequest(error?.status !== 0);

            return this.readBlobError(error).pipe(
              switchMap(readable => this.isAfterLogout(readable).pipe(
                switchMap(afterLogout => {
                  if (afterLogout) {
                    return EMPTY;
                  }

                  // Before settleError: an expired token ends the session there, and
                  // renewing it is exactly what makes that unnecessary.
                  if (this.canRetryAfterRefresh(request, readable)) {
                    return this.refreshAndRetry(clonedRequest, next, readable);
                  }

                  return this.settleError(readable);
                })
              ))
            );
          }),
          finalize(() => {
            if (shouldShowLoader) {
              setTimeout(() => {
                this.loaderService.hide();
              }, 500);
            }
          })
        );
      })
    );
  }
}
